import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from 'services/repositories/user/user';
import {
  ConfirmationModalIds,
  setConfirmationModalHiddenForever,
} from 'support/helpers/confirmationModals/confirmationModals';
import { Connectors, MessageV2DTO } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { DontShowAgainCheckbox } from 'components/DontShowAgainCheckbox/DontShowAgainCheckbox';
import { Button } from 'components/Form/Button/Button';

type ReviewApprovalConfirmationModalProps = {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  message: MessageV2DTO;
};

export const ReviewApprovalConfirmationModal: FC<ReviewApprovalConfirmationModalProps> = ({
  open,
  onConfirm,
  onCancel,
  message,
}) => {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const [hideModalForever, setHideModalForever] = useState(false);
  const receiverConnector = message.receiver_integration?.connector;
  const isOtherConnector = receiverConnector !== Connectors.WECLAPP && receiverConnector !== Connectors.XENTRAL_V3;
  const translationConnectorSuffix = isOtherConnector
    ? 'other'
    : receiverConnector === Connectors.XENTRAL_V3
      ? 'xentral'
      : 'weclapp';

  const handleConfirmApproval = () => {
    if (hideModalForever) {
      setConfirmationModalHiddenForever(ConfirmationModalIds.ReviewApprovalConfirmation, currentUser?.id || '');
    }
    onConfirm();
  };
  return (
    <BasePopup open={open} setOpen={onCancel}>
      <BasePopup.Body>
        <h1 className="text-lg font-semibold text-gray-900">
          {t(`transactions:show.sections.alert.review.confirmApproval.title.${translationConnectorSuffix}`, {
            documentType: t(`common:messageTypes.plural.${message.type}`),
          })}
        </h1>
        <div className="mt-2 flex flex-col gap-6">
          <p className=" text-sm text-gray-500">
            {t(`transactions:show.sections.alert.review.confirmApproval.description.${translationConnectorSuffix}`, {
              documentType: t(`common:messageTypes.singular.${message.type}`).toLowerCase(),
            })}
          </p>
          <DontShowAgainCheckbox checked={hideModalForever} setChecked={setHideModalForever} />
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} analyticsId="pending-review-banner-cancel-approval">
              {t('common:cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmApproval}
              analyticsId="pending-review-banner-confirm-approval"
            >
              {t('transactions:show.sections.alert.review.confirmApproval.approve')}
            </Button>
          </div>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};

/**
 * i18n
 * t('transactions:show.sections.alert.review.confirmApproval.title.weclapp')
 * t('transactions:show.sections.alert.review.confirmApproval.title.xentral')
 * t('transactions:show.sections.alert.review.confirmApproval.title.other')
 *
 * t('transactions:show.sections.alert.review.confirmApproval.description.weclapp')
 * t('transactions:show.sections.alert.review.confirmApproval.description.xentral')
 * t('transactions:show.sections.alert.review.confirmApproval.description.other')
 */
